import { Injectable } from '@angular/core';
import { Nullable } from '../../common/domain/types/types';
import { LocalFileStore } from '../domain/local-file-store';
import { Storage } from '../../storage/domain/storage';

type LocalFileStoreRecord = {
  id: string;
  folder: string;
  filename: string;
  kind: 'blob' | 'data_url';
  payload: Blob | string;
  updatedAt: number;
};

type RuntimeObjectUrl = {
  url: string;
  references: number;
};

@Injectable({
  providedIn: 'root',
})
export class BrowserLocalFileStore extends LocalFileStore {
  private readonly storageKeyPrefix = 'local_file_store:';
  private readonly objectUrlsByRecordId = new Map<string, RuntimeObjectUrl>();

  constructor(private readonly storage: Storage) {
    super();
  }

  async saveBlob(folder: string, filename: string, blob: Blob): Promise<void> {
    const recordId = this.buildRecordId(folder, filename);
    const storageKey = this.buildStorageKey(recordId);
    const blobRecord: LocalFileStoreRecord = {
      id: recordId,
      folder,
      filename,
      kind: 'blob',
      payload: blob,
      updatedAt: Date.now(),
    };

    await this.storage.set(storageKey, blobRecord);

    const storedRecord = await this.storage.get(storageKey);
    const canUseBlobDirectly = this.hasBlobPayload(storedRecord);

    if (!canUseBlobDirectly) {
      const dataUrlPayload = await this.convertBlobToDataUrl(blob);
      const dataUrlRecord: LocalFileStoreRecord = {
        id: recordId,
        folder,
        filename,
        kind: 'data_url',
        payload: dataUrlPayload,
        updatedAt: Date.now(),
      };

      await this.storage.set(storageKey, dataUrlRecord);
    }

    this.revokeObjectUrlForRecord(recordId);
  }

  async fileExists(folder: string, filename: string): Promise<boolean> {
    const record = await this.getRecord(folder, filename);
    return !!record;
  }

  async resolveLocalUrl(folder: string, filename: string): Promise<Nullable<string>> {
    const record = await this.getRecord(folder, filename);
    if (!record) {
      return null;
    }

    const blob = await this.extractBlob(record);
    if (!blob) {
      return null;
    }

    return this.acquireObjectUrl(record.id, blob);
  }

  releaseLocalUrl(folder: string, filename: string): void {
    const recordId = this.buildRecordId(folder, filename);
    const runtimeObjectUrl = this.objectUrlsByRecordId.get(recordId);

    if (!runtimeObjectUrl) {
      return;
    }

    runtimeObjectUrl.references -= 1;

    if (runtimeObjectUrl.references > 0) {
      return;
    }

    URL.revokeObjectURL(runtimeObjectUrl.url);
    this.objectUrlsByRecordId.delete(recordId);
  }

  async buildUniqueFilename(folder: string, filename: string): Promise<string> {
    const extension = this.getExtension(filename);
    const baseName = this.getFilenameWithoutExtension(filename);

    let candidate = filename;
    let counter = 1;

    while (await this.fileExists(folder, candidate)) {
      candidate = extension ? `${baseName} (${counter}).${extension}` : `${baseName} (${counter})`;
      counter += 1;
    }

    return candidate;
  }

  async pruneFilesOlderThan(cutoffTimestamp: number): Promise<number> {
    const keys = await this.storage.keys();
    const localFileStoreKeys = keys.filter((key) => key.startsWith(this.storageKeyPrefix));
    let removedCount = 0;

    for (const key of localFileStoreKeys) {
      const maybeRecord = await this.storage.get(key);
      if (!this.isLocalFileStoreRecord(maybeRecord)) {
        continue;
      }

      if (maybeRecord.updatedAt >= cutoffTimestamp) {
        continue;
      }

      this.revokeObjectUrlForRecord(maybeRecord.id);
      await this.storage.remove(key);
      removedCount += 1;
    }

    return removedCount;
  }

  async clearCachedFiles(): Promise<number> {
    const keys = await this.storage.keys();
    const localFileStoreKeys = keys.filter((key) => key.startsWith(this.storageKeyPrefix));
    let removedCount = 0;

    for (const key of localFileStoreKeys) {
      const maybeRecord = await this.storage.get(key);
      if (this.isLocalFileStoreRecord(maybeRecord)) {
        this.revokeObjectUrlForRecord(maybeRecord.id);
      }

      await this.storage.remove(key);
      removedCount += 1;
    }

    return removedCount;
  }

  private async getRecord(folder: string, filename: string): Promise<Nullable<LocalFileStoreRecord>> {
    const recordId = this.buildRecordId(folder, filename);
    const storageKey = this.buildStorageKey(recordId);
    const storedRecord = await this.storage.get(storageKey);

    if (!storedRecord) {
      return null;
    }

    return storedRecord as LocalFileStoreRecord;
  }

  private acquireObjectUrl(recordId: string, blob: Blob): string {
    const existingObjectUrl = this.objectUrlsByRecordId.get(recordId);

    const webView = (window as any)?.Ionic?.WebView ?? null;
    if (existingObjectUrl) {
      existingObjectUrl.references += 1;
      return webView?.convertFileSrc(existingObjectUrl.url) ?? existingObjectUrl.url;
    }

    const url = URL.createObjectURL(blob);

    this.objectUrlsByRecordId.set(recordId, {
      url,
      references: 1,
    });

    return webView?.convertFileSrc(url) ?? url;
  }

  private revokeObjectUrlForRecord(recordId: string): void {
    const runtimeObjectUrl = this.objectUrlsByRecordId.get(recordId);

    if (!runtimeObjectUrl) {
      return;
    }

    URL.revokeObjectURL(runtimeObjectUrl.url);
    this.objectUrlsByRecordId.delete(recordId);
  }

  private async extractBlob(record: LocalFileStoreRecord): Promise<Nullable<Blob>> {
    if (record.kind === 'blob' && record.payload instanceof Blob) {
      return record.payload;
    }

    if (record.kind === 'data_url' && typeof record.payload === 'string') {
      return this.convertDataUrlToBlob(record.payload);
    }

    return null;
  }

  private hasBlobPayload(value: unknown): value is LocalFileStoreRecord {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const maybeRecord = value as Partial<LocalFileStoreRecord>;
    return maybeRecord.kind === 'blob' && maybeRecord.payload instanceof Blob;
  }

  private isLocalFileStoreRecord(value: unknown): value is LocalFileStoreRecord {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const maybeRecord = value as Partial<LocalFileStoreRecord>;
    const hasValidKind = maybeRecord.kind === 'blob' || maybeRecord.kind === 'data_url';
    const hasStringIdentifiers = typeof maybeRecord.id === 'string' && typeof maybeRecord.folder === 'string';
    const hasFilename = typeof maybeRecord.filename === 'string';
    const hasUpdatedAt = typeof maybeRecord.updatedAt === 'number';

    return hasValidKind && hasStringIdentifiers && hasFilename && hasUpdatedAt;
  }

  private async convertBlobToDataUrl(blob: Blob): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string) ?? '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private convertDataUrlToBlob(dataUrl: string): Nullable<Blob> {
    const parts = dataUrl.split(',');
    if (parts.length !== 2) {
      return null;
    }

    const metadata = parts[0];
    const base64Content = parts[1];
    const mimeMatch = metadata.match(/data:(.*?);base64/);
    const mimeType = mimeMatch?.[1] ?? 'application/octet-stream';

    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let index = 0; index < binaryString.length; index += 1) {
      bytes[index] = binaryString.charCodeAt(index);
    }

    return new Blob([bytes], { type: mimeType });
  }

  private buildRecordId(folder: string, filename: string): string {
    return `${folder}/${filename}`;
  }

  private buildStorageKey(recordId: string): string {
    return `${this.storageKeyPrefix}${recordId}`;
  }

  private getFilenameWithoutExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex <= 0) {
      return filename;
    }

    return filename.substring(0, lastDotIndex);
  }

  private getExtension(filename: string): string {
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1) {
      return '';
    }

    return filename.substring(lastDotIndex + 1);
  }
}
